import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Platform } from 'react-native';
import { connect, useSelector } from 'react-redux';

import analytics from '../../../modules/analytics';
import { getAndroidVersion, getIOSVersion } from '../../../modules/AppInfo';
import { SidebarToggle } from './SidebarToggle';
import DeprecatedSideBarToggle from './DeprecatedSidebarToggle';
import SidebarContainer from '../Sidebar/SidebarContainer';
import NavigateBack from '../NavigateBack';
import { useShouldShowClientNavBar } from '../../consumer/ClientNavBar/hooks';
import {
  CLIENT_APPTS_LIST,
  CLIENT_CONTACT_INFORMATION_ROUTE,
  CLIENT_MORE_MENU,
  SEARCH_ROUTE,
} from '../../../route-names';

import './SidebarToggle.module.scss';
import {
  useIsMobile,
  useIsMobileOrDesktopNarrow,
  useUnmount,
} from '../../../hooks';
import store from '../../../store';
import { selectors } from '../../../store/CurrentUser.model';

const TOGGLE_ENABLED_STATES = [
  '',
  'start',
  'home',
  'consumer.account',
  CLIENT_APPTS_LIST,
  CLIENT_MORE_MENU,
  SEARCH_ROUTE,
  'consumer.discover',
  'profile',
  'virtual_landing',
];

const TOGGLE_DISABLED_STATES = [
  'consumer.trackedsignup',
  CLIENT_CONTACT_INFORMATION_ROUTE,
];

/**
 * Takes care of knowing when to display:
 * a) Sidebar Hamburger Menu (with logic setup)
 * b) A Navigational Back Button
 * c) Neither of those (aka renders nothing)
 *
 * As it stands, this component is better suited to be renamed to
 * something that reflects what it actually does.
 */
const SidebarToggleContainer = ({
  stateName,
  toggle,
  close,
  shouldDisplayOnDesktopSize = false,
  route,
  ...restProps
}) => {
  const [displaySidebar, setDisplaySidebar] = useState<boolean>(false);
  const userSelector = store.select.user;
  const isLandingRedesignEnabled = useSelector(userSelector.inLandingRedesign2024);

  const isMobileDeprecated = useIsMobile();
  const isMobileRedesign = useIsMobileOrDesktopNarrow();
  const comesFromSearch = route?.params?.ncd_source === 'generic_search'
    || route?.params?.sid !== undefined;

  const isMobile = isLandingRedesignEnabled ? isMobileRedesign : isMobileDeprecated;
  const isLoggedIn = useSelector(selectors.isLoggedIn);
  const isProvider = useSelector(selectors.isProvider);
  const {
    shouldShowClientNav,
    loading,
    isChatGeneralSwitchEnabled,
  } = useShouldShowClientNavBar();

  const isIOS = !!getIOSVersion();
  const isAndroid = !!getAndroidVersion();
  // always show when not in app or when in app under specific routes
  const visible = !(isIOS || isAndroid) || TOGGLE_ENABLED_STATES.some(item => (
    stateName.indexOf(item) !== -1
  ));
  // explicitly hide for some particular routes
  const hidden = !visible || TOGGLE_DISABLED_STATES.some(item => (
    stateName.indexOf(item) !== -1
  ));

  useUnmount(() => {
    close();
  }, []);

  if (hidden && Platform.OS === 'web') {
    return null;
  }

  const handleSidebarToggle = e => {
    analytics.track('sidebar_click', { item: 'initial_tap' });
    toggle();
    e.preventDefault();
    e.stopPropagation();
  };

  if (loading) {
    return null;
  }

  // When we are showing the client nav bar, there are some routes that we
  // should display the back button instead
  const shouldShowClientNavLogic = isLandingRedesignEnabled
    ? isChatGeneralSwitchEnabled : shouldShowClientNav;
  if (shouldShowClientNavLogic) {
    const routesToShowMenu = [
      'home',
      'land',
      'start',
      SEARCH_ROUTE,
      'provider_vanity',
    ];

    let shouldDisplayBackButton = !routesToShowMenu.includes(stateName);

    if (isLandingRedesignEnabled) {
      shouldDisplayBackButton = shouldDisplayBackButton && comesFromSearch;
    }
    // We should only display back button when we don't match the above routes
    if (shouldDisplayBackButton) {
      return (
        <NavigateBack isLandingRedesignEnabled={isLandingRedesignEnabled} />
      );
    }
  }

  if (!shouldDisplayOnDesktopSize && !isMobile) {
    return null;
  }

  const handleToggle = () => {
    analytics.track('StyleSeat HomePage Hamburger Clicked', {
      loggedIn: isLoggedIn,
      isProvider,
      isClient: isLoggedIn && !isProvider,
    });
    setDisplaySidebar(!displaySidebar);
  };

  if (isLandingRedesignEnabled) {
    return (
      <React.Fragment>
        <SidebarToggle
          toggle={handleToggle}
          shouldDisplayOnDesktopSize={shouldDisplayOnDesktopSize}
          size={isMobile ? 24 : 36}
        />
        <SidebarContainer
          isSidebarDisplayed={displaySidebar}
          handleClose={() => setDisplaySidebar(false)}
          {...restProps}
        />
      </React.Fragment>
    );
  }

  return (
    <DeprecatedSideBarToggle toggle={handleSidebarToggle} {...restProps} />
  );
};

SidebarToggleContainer.propTypes = {
  /**
   * The name of the current navigation state
   */
  stateName: PropTypes.string.isRequired,
  /**
   * Toggles the open state of the sidebar
   */
  toggle: PropTypes.func.isRequired,
  /**
   * Close the open state of the sidebar
   */
  close: PropTypes.func.isRequired,
  /**
   * Whether or not the sidebar should be displayed on desktop size
   */
  shouldDisplayOnDesktopSize: PropTypes.bool,
};

const mapStateToProps = ({ route }) => ({
  stateName: route.name,
  route,
});

const mapDispatchToProps = dispatch => ({
  toggle: dispatch.sidebar.toggle,
  close: dispatch.sidebar.close,
});

export default connect(mapStateToProps, mapDispatchToProps)(SidebarToggleContainer);
